import type { FormDetailResponse } from "@/types/entities/formality-request";
import { cn } from "@/utils/theme";
import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import renderers from "./renderers";
import type { Fields } from "../common/types";
import type { CommentWithGroup } from "../deny-reason-show-modal";
import { hasFieldChangeHistory } from "../serialization/serialization";
import { getLastFilledField } from "@/utils/filled-fields";

const columnHelper = createColumnHelper<Fields>();

export const collectFieldComments = (
  assignment: FormDetailResponse["form_assignments"][number] | undefined,
  fieldId: string,
): CommentWithGroup[] => {
  const comments: CommentWithGroup[] = [];
  if (!assignment?.revisions) return comments;

  const latestRevision = [...(assignment.revisions ?? [])].sort(
    (a, b) =>
      new Date(b.created_at ?? 0).getTime() -
      new Date(a.created_at ?? 0).getTime(),
  )[0];

  if (!latestRevision) return comments;

  // A single revision can hold multiple corrections for the same group across
  // iterations. Sort chronologically and overwrite per group so the latest
  // correction (most recent iteration) wins for each group. Corrections carry
  // no top-level timestamp, so order by their first field correction's date.
  const correctionTime = (c: {
    field_corrections?: { created_at?: string | null }[];
  }) => new Date(c.field_corrections?.[0]?.created_at ?? 0).getTime();

  const corrections = [...(latestRevision.corrections ?? [])].sort(
    (a, b) => correctionTime(a) - correctionTime(b),
  );

  const latestByGroup = new Map<string, CommentWithGroup>();

  for (const correction of corrections) {
    const groupName = correction?.group?.name ?? "Comentario";
    for (const fieldCorrection of correction.field_corrections ?? []) {
      if (fieldCorrection.filled_field_id !== fieldId) continue;
      const comment = fieldCorrection.comments?.[0];
      if (!comment?.text) continue;
      latestByGroup.set(groupName, { text: comment.text, groupName });
      break;
    }
  }

  return [...latestByGroup.values()];
};

const buildCorrectionPriorityMap = (
  assignment: FormDetailResponse["form_assignments"][number] | undefined,
) => {
  const map = new Map();
  if (!assignment?.revisions) return map;

  for (const revision of assignment.revisions) {
    for (const correction of revision.corrections ?? []) {
      for (const fieldCorrection of correction.field_corrections ?? []) {
        const existing = map.get(fieldCorrection.filled_field_id);
        if (
          !existing ||
          (existing.status !== "DENIED" && fieldCorrection.status === "DENIED")
        ) {
          map.set(fieldCorrection.filled_field_id, fieldCorrection);
        }
      }
    }
  }
  return map;
};

export const tableColumns = (
  assigment: FormDetailResponse["form_assignments"][number] | undefined,
  showReasonModal: (reasons: CommentWithGroup[]) => void,
  fieldStartIndex: number = 0,
  onShowFieldHistory?: (field: Fields) => void,
) => {
  // Un formulario que pasó por un nodo de corrección tiene revisiones: solo ahí
  // hay historial que mostrar, y es la misma condición que habilita la columna
  // "Corrección".
  const hasRevisions = (assigment?.revisions?.length ?? 0) > 0;
  const correctionPriorityMap = buildCorrectionPriorityMap(assigment);

  return [
    columnHelper.display({
      id: "field_number",
      header: "#",
      meta: {
        className:
          "align-top min-w-[48px] px-3 py-[32px] text-center align-center",
      },
      cell: ({ row }) => (
        <span className="text-body text-base tabular-nums">
          {fieldStartIndex + row.index + 1}
        </span>
      ),
    }),
    columnHelper.accessor("name", {
      header: "Tipo de dato",
      meta: {
        className:
          "align-top min-w-0 w-[45%] max-w-[50%] lg:w-[353px] lg:min-w-[353px] lg:max-w-[353px] !whitespace-normal break-words overflow-hidden",
      },
      cell: (info) => {
        if (!info.getValue()) {
          return <p>&nbsp;</p>;
        }
        const field = info.row.original;

        return (
          <div className="w-full flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={
                  field.required
                    ? "text-info text-xs font-semibold"
                    : "text-body-quiet text-xs"
                }
              >
                {field.required ? "Requerido" : "Opcional"}
              </span>
            </div>
            <p className="font-semibold text-body-loud text-size-body wrap-break-word min-w-0">
              {field.label}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("filled_fields", {
      header: "Valor",
      meta: {
        className:
          "w-full overflow-hidden !whitespace-normal break-all align-center",
      },
      cell: (info) => {
        const fieldArr = info.getValue();
        const obj = info.row.original;
        const field = info.row.original;
        if ((!fieldArr || fieldArr.length === 0) && obj.type !== "switch") {
          return <span className="text-on-disabled-quiet">Vacío</span>;
        }

        // La fila más reciente manda aunque venga vacía: saltarla mostraba el
        const targetField = getLastFilledField(fieldArr);
        const value = targetField?.value;

        const defaultContent: React.ReactNode = (
          <p className="break-normal whitespace-pre-wrap text-size-body">
            {value?.toString() ?? ""}
          </p>
        );

        // Normalize camelCase types to snake_case for renderer lookup.
        const normalizedType = obj.type
          .replace(/([a-z])([A-Z])/g, "$1_$2")
          .toLowerCase();
        const renderer = renderers[normalizedType];

        let content: React.ReactNode;
        if (obj.type === "interoperability") {
          content = (
            <renderers.interoperability
              obj={obj}
              value={value}
              targetField={targetField}
              fieldArr={fieldArr}
            />
          );
        } else if (renderer) {
          content = renderer({ obj, value, targetField, fieldArr });
        } else if (
          (value === null ||
            value === undefined ||
            value === "" ||
            (Array.isArray(value) && value.length === 0)) &&
          obj.type !== "file"
        ) {
          content = <span className="text-on-disabled-quiet">Vacío</span>;
        } else if (Array.isArray(value)) {
          // Multi-value arrays with no dedicated renderer fall back to plain
          content = (
            <p className="break-normal whitespace-pre-wrap text-size-body">
              {(value as Array<string>).join(", ")}
            </p>
          );
        } else {
          content = defaultContent;
        }

        // Sin cambios registrados el modal solo mostraría su estado vacío, así
        // que el acceso al historial no se dibuja.
        if (
          !hasRevisions ||
          !onShowFieldHistory ||
          !hasFieldChangeHistory(field, assigment)
        ) {
          return content;
        }

        return (
          <div className="flex w-full items-center gap-2">
            <div className="min-w-0 flex-1">{content}</div>
            <Button
              size="icon"
              variant="quiet"
              type="button"
              data-testid="correction-history"
              aria-label="correction-history"
              className="shrink-0"
              onClick={() => onShowFieldHistory(field)}
            >
              <Icon name="clock" type="far" />
            </Button>
          </div>
        );
      },
    }),
    ...(hasRevisions
      ? [
          columnHelper.display({
            id: "correction",
            header: "Corrección",
            meta: {
              className: "w-[48px] border-transparent border-x-2 align-center",
            },
            cell: ({ row }) => {
              const filled_field =
                row.original.filled_fields?.[
                  row.original.filled_fields.length - 1
                ];
              // Prefer the correction resolved from the revision-level
              // field_corrections (DENIED over APPROVED); fall back to the
              // single correction the API flattened into the field.
              const correction =
                correctionPriorityMap.get(filled_field?.id ?? "") ??
                filled_field?.correction;
              if (!correction) {
                return <p>&nbsp;</p>;
              }
              return (
                <div className="relative">
                  {correction?.status === "DENIED" && (
                    <Badge
                      size="xs"
                      variant="danger"
                      fill="solid"
                      className="rounded-full size-5 flex items-center justify-center absolute -top-3 -right-2"
                      onClick={() => {
                        const fieldComments = collectFieldComments(
                          assigment,
                          filled_field?.id ?? "",
                        );
                        showReasonModal(
                          fieldComments.length > 0
                            ? fieldComments
                            : [
                                {
                                  text:
                                    correction?.comments?.[0]?.text || "RAZON",
                                  groupName: "Comentario",
                                },
                              ],
                        );
                      }}
                    >
                      <Icon
                        name="comment"
                        type="fas"
                        className="text-2xs text-on-solid"
                      />
                    </Badge>
                  )}
                  <Badge
                    shape="square"
                    size="sm"
                    className="size-6 flex items-center justify-center rounded-lg"
                    variant={
                      correction?.status === "APPROVED" ? "success" : "danger"
                    }
                  >
                    <Icon
                      name={
                        correction.status === "APPROVED" ? "check" : "times"
                      }
                      type="fas"
                      className={cn(
                        "text-xs",
                        correction?.status === "APPROVED" &&
                          "text-success-quiet",
                        correction?.status === "DENIED" && "text-danger-quiet",
                      )}
                    />
                  </Badge>
                </div>
              );
            },
          }),
        ]
      : []),
  ] as ColumnDef<Fields>[];
};
